// ===================================================
// App.js — 앱의 최상위 파일
//
// 역할:
//   - 각 훅을 조합해 앱 전체 상태를 연결
//   - AppScreens 컴포넌트에 상태와 핸들러를 전달
//
// 비즈니스 로직은 아래 훅 파일에 분리되어 있습니다:
//   - useAuth.js              : 로그인/로그아웃 상태 관리
//   - useGuestMigration.js    : 게스트 기록 → 계정 이전
//   - useMemoryResurfacing.js : 기억 되살리기 + 읽지 않은 채팅 알림
//   - useSessionHandlers.js   : AI 호출 핸들러 (기록 저장, 수정 등)
//   - useSessionManager.js    : 화면 전환 및 세션 목록 관리
//
// 화면 렌더링은 AppScreens.js가 담당합니다.
// ===================================================

import { useState, useEffect } from 'react';
import './App.css';
import './modeTheme.css';

import { Capacitor } from '@capacitor/core';
import { supabase } from './supabase';
import { deleteAllSessions, clearChatMessages, deleteAccount } from './storage';
import { deleteAllGuestSessions } from './utils/guestStorage';
import { restoreStorageFromNative, appRead, appWrite } from './utils/appStorage';
import { useAuth } from './hooks/useAuth';
import { useSessionManager } from './hooks/useSessionManager';
import { useGuestMigration } from './hooks/useGuestMigration';
import { useMemoryResurfacing } from './hooks/useMemoryResurfacing';
import { useNotificationRouting } from './hooks/useNotificationRouting';
import { useSessionHandlers } from './hooks/useSessionHandlers';
import { useGoogleCalendar } from './hooks/useGoogleCalendar';
import { AppScreens } from './AppScreens';
import { ONBOARDING_KEY } from './OnboardingModal';

// 네이티브 전용 모듈은 동적 import()로 로드 (웹 번들 크기 절감)
const isNative = Capacitor.isNativePlatform();

function App() {
  // ── 다크 모드 비활성화: 항상 라이트 모드 강제 ────────────
  // darkTheme.css가 불러와지지 않으므로 data-theme 속성 자체가 불필요하지만,
  // 브라우저 localStorage에 이전 'dark' 값이 남아있을 경우 대비해 'light'로 고정
  document.documentElement.setAttribute('data-theme', 'light');

  // ── 인증 상태 ─────────────────────────────────────────────
  // onGoogleTokens 콜백은 아래 useGoogleCalendar에서 정의
  const [pendingGoogleTokens, setPendingGoogleTokens] = useState(null);
  const { user, authLoading, passwordRecovery, setPasswordRecovery, isMaster } = useAuth(
    (accessToken, refreshToken) => setPendingGoogleTokens({ accessToken, refreshToken })
  );

  // ── 구글 캘린더 연동 ─────────────────────────────────────
  const googleCalendar = useGoogleCalendar(user);

  // ── 화면 전환 및 세션 목록 ────────────────────────────────
  const {
    screen, loading, error, mode,
    initialText, currentQuestions, questionsLoading, sessionRounds,
    decisionAxes,
    sessions, sessionsLoaded, sessionsError, selectedSession,
    keywordsMode, selectedKeyword,
    mobileDrawerOpen,
    lifeSummary, setLifeSummary,
    setMode, setScreen, setLoading, setError,
    setInitialText, setCurrentQuestions, setQuestionsLoading, setSessionRounds,
    setDecisionAxes,
    refreshSessions, resetSession,
    closeMobileDrawer,
    goBack,
    handleSelectSession, handleHome, handleDeleteSession,
    handleSelectKeywords, handleKeywordClick, handleBackFromKeywordSearch,
    handleOpenSearch, handleOpenSettings, handleOpenChat, handleOpenCalendar, handleOpenAdmin, handleOpenPushSettings,
  } = useSessionManager(user);

  // ── UI 상태 (게스트 배너, 오프라인, 토스트) ───────────────
  const [showAuth, setShowAuth] = useState(false);
  const [guestBannerDismissed, setGuestBannerDismissed] = useState(false);
  const [isOffline, setIsOffline] = useState(false);
  const [toast, setToast] = useState(null);
  // 온보딩 모달: 최초 실행 시 한 번만 표시
  const [showOnboarding, setShowOnboarding] = useState(!appRead(ONBOARDING_KEY));
  // onNavigate: 토스트 클릭 시 호출할 함수 (채팅 이동 등). 없으면 단순 닫기.
  const showToast = (message, type = 'success', onNavigate = null) => setToast({ message, type, onNavigate });

  // ── 플래너 모드 캘린더 UI 상태 ────────────────────────────
  const [showCalendarPrompt, setShowCalendarPrompt] = useState(false); // 미연동 안내 카드
  const [quickQuestion, setQuickQuestion] = useState(null);            // 한 줄 질문 카드 { question, events }

  // ── 게스트 → 계정 마이그레이션 ───────────────────────────
  useGuestMigration({ user, refreshSessions, showToast, setShowAuth });

  // ── 기억 되살리기 + 읽지 않은 채팅 알림 ──────────────────
  const { hasUnreadChat, setHasUnreadChat } = useMemoryResurfacing({ user, sessions, sessionsLoaded, showToast });

  // ── 알림 탭 시 적절한 화면으로 라우팅 ───────────────────
  useNotificationRouting({ setScreen, user });

  // ── AI 호출 핸들러 ────────────────────────────────────────
  const { handleLandingNext, handleContinue, handleEnd, handleRegenerateQuestion, handleReportQuestion, handleFreewriteSave, handleUpdateSession, handleChatSend, handleQuickQuestionSave, handleTogglePinned } = useSessionHandlers({
    user, sessions, mode, initialText, sessionRounds,
    decisionAxes,
    lifeSummary, setLifeSummary,
    setError, setLoading, setInitialText, setCurrentQuestions, setQuestionsLoading, setSessionRounds,
    setDecisionAxes,
    setScreen, refreshSessions, resetSession, showToast,
    onOpenChat: handleOpenChat,
    // 구글 캘린더 연동
    googleAccessToken: googleCalendar.googleAccessToken,
    selectedCalendarId: googleCalendar.selectedCalendarId,
    isCalendarConnected: googleCalendar.isCalendarConnected,
    onShowCalendarPrompt: () => setShowCalendarPrompt(true),
    onShowQuickQuestion: (question, events) => setQuickQuestion({ question, events }),
  });

  // ── 구글 OAuth 토큰 수신 시 캘린더 훅에 전달 ────────────
  const { handleGoogleTokens } = googleCalendar;
  useEffect(() => {
    if (pendingGoogleTokens && user) {
      handleGoogleTokens(
        pendingGoogleTokens.accessToken,
        pendingGoogleTokens.refreshToken,
      );
      setPendingGoogleTokens(null);
    }
  }, [pendingGoogleTokens, user, handleGoogleTokens]);

  // ── Edge Function 워밍업 (Cold Start 제거) ──
  // 앱 로드 시 한 번 호출하여 서버가 미리 깨어나게 합니다.
  useEffect(() => { fetch('/api/warmup').catch(() => {}); }, []);

  // ── 네이티브 모듈 초기화 (동적 import — 웹에서는 실행 안 됨) ──
  useEffect(() => { restoreStorageFromNative(); }, []); // iOS 저장소 복원
  useEffect(() => {
    if (!isNative) return;
    // 네이티브에서만 키보드·상태바·네트워크 모듈 로드
    import('./native/keyboard').then(m => m.initKeyboardHandler());
    import('./native/statusbar').then(m => m.initStatusBar(false));
    import('./native/network').then(m => m.initNetworkWatcher(
      () => setIsOffline(true),
      () => setIsOffline(false),
    ));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 스플래시 스크린 제거 ──────────────────────────────────
  useEffect(() => {
    if (!isNative || !sessionsLoaded) return;
    import('@capacitor/splash-screen').then(m => m.SplashScreen.hide({ fadeOutDuration: 200 }));
  }, [sessionsLoaded]);

  // ── Android 뒤로가기 버튼 처리 ──────────────────────────
  useEffect(() => {
    if (!isNative) return;
    let cleanup;
    import('@capacitor/app').then(m => {
      const handler = m.App.addListener('backButton', () => {
        if (screen !== 'landing') {
          goBack();
        } else {
          m.App.minimizeApp();
        }
      });
      cleanup = () => handler.then(h => h.remove());
    });
    return () => { if (cleanup) cleanup(); };
  }, [screen, goBack]);

  // ── 푸시 알림 초기 예약 (네이티브 전용) ─────────────────
  useEffect(() => {
    if (!isNative) return;
    import('./native/notifications').then(n => {
      n.scheduleReengagementPushes();  // E. 3/7/14일 후 이탈 방지
      n.scheduleWeeklyReviewPush();    // C. 매주 일요일 10시 주간 회고
      n.cancelMemoryResurfacingPush(); // B. 앱 열렸으니 인앱이 처리 → 푸시 취소
      // A. 일일 리마인더 재등록 (앱 업데이트 후 알림 초기화 복구)
      const reminderStored = appRead('omsal_reminder_enabled');
      if (reminderStored === 'true' || reminderStored === null) {
        const hour = parseInt(appRead('omsal_reminder_hour') || '21', 10);
        n.scheduleJournalReminder(hour, 0);
      }
    });
  }, []);

  // ── 앱 포그라운드 복귀 시 ──────────────────────────────
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        if (user) refreshSessions();
        if (isNative) {
          import('./native/notifications').then(n => {
            n.scheduleReengagementPushes();
            n.cancelMemoryResurfacingPush();
          });
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, refreshSessions]);

  // ── 리포트 탭: 비로그인 차단 ────────────────────────────
  const handleReportGuestBlock = !user
    ? () => showToast('리포트는 로그인 후 이용할 수 있어요', 'error')
    : null;

  // ── 채팅 탭 열기 ─────────────────────────────────────────
  const handleOpenChatWithReadMark = () => {
    if (!user) {
      showToast('나와의 대화는 로그인 후 이용할 수 있어요', 'error');
      handleOpenSettings();
      return;
    }
    handleOpenChat();
    appWrite('lastChatOpenedAt', new Date().toISOString());
    setHasUnreadChat(false);
    if (isNative) {
      import('./native/badge').then(m => m.clearBadge());
      import('./native/notifications').then(m => m.cancelNewChatMessagePush());
    }
  };

  // ── 전체 삭제 / 로그아웃 ─────────────────────────────────
  const handleDeleteAll = async () => {
    if (user) {
      await deleteAllSessions();
      await clearChatMessages();
    } else {
      deleteAllGuestSessions();
    }
    await refreshSessions();
    handleHome();
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  // ── 계정 완전 삭제 ────────────────────────────────────────
  // 모든 데이터 + 인증 계정을 서버에서 삭제한 뒤 로그아웃 처리
  const handleDeleteAccount = async () => {
    await deleteAccount();
    await supabase.auth.signOut();
  };

  // ── 렌더링 ───────────────────────────────────────────────
  return (
    <AppScreens
      authLoading={authLoading}
      passwordRecovery={passwordRecovery}
      setPasswordRecovery={setPasswordRecovery}
      user={user}
      showAuth={showAuth}
      setShowAuth={setShowAuth}
      mode={mode}
      isOffline={isOffline}
      guestBannerDismissed={guestBannerDismissed}
      setGuestBannerDismissed={setGuestBannerDismissed}
      sessions={sessions}
      sessionsError={sessionsError}
      toast={toast}
      setToast={setToast}
      screen={screen}
      keywordsMode={keywordsMode}
      selectedKeyword={selectedKeyword}
      selectedSession={selectedSession}
      mobileDrawerOpen={mobileDrawerOpen}
      closeMobileDrawer={closeMobileDrawer}
      loading={loading}
      error={error}
      sessionRounds={sessionRounds}
      currentQuestions={currentQuestions}
      questionsLoading={questionsLoading}
      hasUnreadChat={hasUnreadChat}
      setScreen={setScreen}
      setMode={setMode}
      handleSelectSession={handleSelectSession}
      handleSelectKeywords={handleSelectKeywords}
      handleOpenSearch={handleOpenSearch}
      handleOpenSettings={handleOpenSettings}
      handleOpenCalendar={handleOpenCalendar}
      handleOpenPushSettings={handleOpenPushSettings}
      handleOpenChatWithReadMark={handleOpenChatWithReadMark}
      goBack={goBack}
      handleHome={handleHome}
      handleKeywordClick={handleKeywordClick}
      handleBackFromKeywordSearch={handleBackFromKeywordSearch}
      handleLandingNext={handleLandingNext}
      handleContinue={handleContinue}
      handleEnd={handleEnd}
      handleRegenerateQuestion={handleRegenerateQuestion}
      handleReportQuestion={handleReportQuestion}
      handleFreewriteSave={handleFreewriteSave}
      handleUpdateSession={handleUpdateSession}
      handleChatSend={handleChatSend}
      handleDeleteSession={handleDeleteSession}
      refreshSessions={refreshSessions}
      handleDeleteAll={handleDeleteAll}
      handleLogout={handleLogout}
      handleDeleteAccount={handleDeleteAccount}
      googleCalendar={googleCalendar}
      showCalendarPrompt={showCalendarPrompt}
      setShowCalendarPrompt={setShowCalendarPrompt}
      quickQuestion={quickQuestion}
      setQuickQuestion={setQuickQuestion}
      handleQuickQuestionSave={handleQuickQuestionSave}
      handleTogglePinned={handleTogglePinned}
      handleReportGuestBlock={handleReportGuestBlock}
      showOnboarding={showOnboarding}
      onOnboardingDone={() => setShowOnboarding(false)}
      isMaster={isMaster}
      handleOpenAdmin={handleOpenAdmin}
    />
  );
}

export default App;
